import React from 'react';
import { Icon, List, Message, Popup, Table } from 'semantic-ui-react';

import { clienteApi, Resultados } from 'src/api';
import { LinkAlumno } from 'src/componentes';
import { Contexto } from 'src/contexto';
import { mesesPlanAnual, mesesPlanIntensivo } from 'src/recursos/mesesEnsayo';
import { compararNombresConApellidosPrimero } from 'src/utilidades';
import { HeaderNombreDeCurso } from '../componentes';
import { ItemConEtiqueta } from '../componentes/ItemConEtiqueta';
import { Alumno, DetallesDelCurso } from './tipos';

type Props = Readonly<{
    ocultar: boolean;
    idCurso: number;
    detalleDelCurso: Resultados<'detallesDelCursoDeId'> | undefined;
    actualizarDetalle: () => Promise<DetallesDelCurso>;
}>;

type ResultadosEnsayosDTO = Resultados<'obtenerPuntajeEnsayosDeCurso'>;
type ResultadoUnEnsayoDTO = NonNullable<NonNullable<ResultadosEnsayosDTO['string']>[NombreEnsayo]>;

interface State {
    resultados?: {
        porId: ResultadosEnsayosDTO;
        ids: readonly string[];
    };
    nombresAlumnos?: ReadonlyMap<string, Alumno>;
    cargando: boolean;
}

type NombreEnsayo = keyof typeof mesesPlanAnual;
const mesesAnual = Object.keys(mesesPlanAnual) as readonly NombreEnsayo[];
const mesesIntensivo = Object.keys(mesesPlanIntensivo) as readonly NombreEnsayo[];

class Puntajes extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public readonly state: State = {
        cargando: false,
    };

    public componentDidMount() {
        if (!this.props.ocultar) {
            this.obtenerDatos().catch();
        }
    }

    public componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevProps.ocultar && !this.props.ocultar && !prevState.resultados) {
            this.obtenerDatos().catch();
        }
    }

    public render() {
        if (this.props.ocultar || !this.props.detalleDelCurso) return null;

        const { cargando: cargandoResultados, resultados, nombresAlumnos } = this.state;
        const mesesEnOrden =
            this.props.detalleDelCurso.plan === 'anual' ? mesesAnual : mesesIntensivo;

        if (cargandoResultados) {
            return <Icon loading name='spinner' size='big' />;
        }

        if (!resultados || !nombresAlumnos) return null;

        return (
            <div>
                <HeaderNombreDeCurso curso={this.props.detalleDelCurso} />

                <Message info compact>
                    No se muestran alumnos sin puntaje registrado ni restringidos.
                </Message>

                <Table celled striped collapsing>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Alumno</Table.HeaderCell>
                            {mesesEnOrden.map(mes => (
                                <Table.HeaderCell key={mes} content={mesesPlanAnual[mes]} />
                            ))}
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {resultados.ids.map(id => {
                            const ensayo = resultados.porId[id];
                            const alumno = nombresAlumnos.get(id)!;

                            return (
                                <Table.Row key={id}>
                                    <LinkAlumno
                                        numeroDeContrato={alumno.numeroDeMatricula}
                                        nombre={{
                                            nombre: alumno.nombre,
                                            patron: '%P %M %N %S',
                                        }}
                                        paraTabla
                                    />

                                    {mesesEnOrden.map(mes => {
                                        const resultado = ensayo && ensayo[mes];
                                        if (!resultado) return <Table.Cell key={id + mes} />;

                                        return <Puntaje key={id + mes} resultado={resultado} />;
                                    })}
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </div>
        );
    }

    private readonly obtenerDatos = async () => {
        if (this.state.cargando) return;

        this.setState({ cargando: true });

        const pDetalle = this.props.actualizarDetalle();
        try {
            const pPuntajes = clienteApi.ejecutar(
                'obtenerPuntajeEnsayosDeCurso',
                this.props.idCurso,
            );

            const { alumnos } = await clienteApi.ejecutar('listaDeAlumnos');
            const mapaAlumnos = new Map(alumnos.map(alu => [alu.id, alu]));
            this.setState({ nombresAlumnos: mapaAlumnos });

            const resultados = await pPuntajes;
            const porId = resultados;
            const ids = Object.keys(resultados)
                .filter(id => mapaAlumnos.has(id))
                .sort((a, b) => {
                    const aluA = mapaAlumnos.get(a);
                    const aluB = mapaAlumnos.get(b);

                    return compararNombresConApellidosPrimero(aluA!.nombre, aluB!.nombre);
                });

            await pDetalle;

            this.setState({ resultados: { porId, ids }, cargando: false });
        } catch (error) {
            this.context.mostrarModalError(error);
            this.setState({ cargando: false });
        }
    };
}

const puntajeStyle = {
    borderBottom: '1px dotted #000',
};
const Puntaje = (props: { resultado: ResultadoUnEnsayoDTO }) => {
    const { resultado } = props;

    const celtaConPuntaje = (
        <Table.Cell textAlign='center'>
            <span style={puntajeStyle}>{resultado.puntaje}</span>
        </Table.Cell>
    );

    return (
        <Popup trigger={celtaConPuntaje} position='top center'>
            <Popup.Content>
                <List>
                    <ItemConEtiqueta etiqueta='Puntaje' valor={resultado.puntaje} />
                    <ItemConEtiqueta etiqueta='Buenas' valor={resultado.buenas} />
                    <ItemConEtiqueta etiqueta='Malas' valor={resultado.malas} />
                    <ItemConEtiqueta etiqueta='Omitidas' valor={resultado.omitidas} />
                </List>
            </Popup.Content>
        </Popup>
    );
};

export default Puntajes;



// WEBPACK FOOTER //
// ./src/modulos/Horarios/DetalleDelCurso/Puntajes.tsx